import React, { useState, useEffect, useRef } from 'react';
import { Send, MessageSquare, PenSquare, X, Search, Trash2, Users } from 'lucide-react';
import { dmAPI, connectionsAPI, groupsAPI } from '../api/client';
import Layout from '../components/Layout';
import '../styles/messages.css';

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return (parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : name[0]).toUpperCase();
}

function timeAgo(iso) {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

export default function Messages() {
  const [conversations, setConversations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [connections, setConnections] = useState([]);
  const [active, setActive] = useState(null); // { type: 'dm'|'group', id, name }
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [composing, setComposing] = useState(false);
  const [composeSearch, setComposeSearch] = useState('');
  const [sidebarSearch, setSidebarSearch] = useState('');
  const [hoveredConv, setHoveredConv] = useState(null);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const inputRef = useRef(null);
  const currentUserId = localStorage.getItem('userId');

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (!active) return;
    loadMessages(active);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(() => loadMessages(active), 5000);
    return () => clearInterval(pollRef.current);
  }, [active]);

  const loadAll = async () => {
    try {
      const [convRes, groupRes, connRes] = await Promise.all([
        dmAPI.getConversations(),
        groupsAPI.getAll(),
        connectionsAPI.getAll(),
      ]);
      setConversations(convRes.data || []);
      setGroups(groupRes.data || []);
      setConnections((connRes.data || []).filter(c => c.status === 'accepted'));
    } catch {}
    setLoadingConvs(false);
  };

  const loadMessages = async (conv) => {
    try {
      const r = conv.type === 'group'
        ? await groupsAPI.getMessages(conv.id)
        : await dmAPI.getMessages(conv.id);
      setMessages(r.data || []);
    } catch {}
  };

  const openDM = (userId, displayName) => {
    const conv = { type: 'dm', id: userId, name: displayName };
    setActive(conv);
    setMessages([]);
    setComposing(false);
    setComposeSearch('');
    setLoadingMsgs(true);
    dmAPI.getMessages(userId)
      .then(r => { setMessages(r.data || []); setLoadingMsgs(false); })
      .catch(() => setLoadingMsgs(false));
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const openGroup = (groupId, groupName) => {
    const conv = { type: 'group', id: groupId, name: groupName };
    setActive(conv);
    setMessages([]);
    setComposing(false);
    setComposeSearch('');
    setLoadingMsgs(true);
    groupsAPI.getMessages(groupId)
      .then(r => { setMessages(r.data || []); setLoadingMsgs(false); })
      .catch(() => setLoadingMsgs(false));
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || !active) return;
    setSending(true);
    const content = input.trim();
    setInput('');
    try {
      const r = active.type === 'group'
        ? await groupsAPI.sendMessage(active.id, content)
        : await dmAPI.send(active.id, content);
      setMessages(prev => [...prev, r.data]);
      if (active.type === 'dm') await dmAPI.getConversations().then(r => setConversations(r.data || []));
    } catch {}
    setSending(false);
  };

  const handleDeleteDM = async (e, userId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation? This cannot be undone.')) return;
    await dmAPI.deleteConversation(userId).catch(() => {});
    setConversations(prev => prev.filter(c => c.user_id !== userId));
    if (active?.type === 'dm' && active.id === userId) {
      setActive(null);
      setMessages([]);
    }
  };

  const handleDeleteMessage = async (msgId) => {
    try {
      await dmAPI.deleteMessage(msgId);
      setMessages(prev => prev.filter(m => m.id !== msgId));
    } catch {}
  };

  const getFriendId = (c) => c.user_id === currentUserId ? c.connected_user_id : c.user_id;

  const convUserIds = new Set(conversations.map(c => c.user_id));
  const freshFriends = connections.filter(c => !convUserIds.has(getFriendId(c)));

  const allFriendsForCompose = [
    ...conversations.map(c => ({ type: 'dm', id: c.user_id, name: c.display_name })),
    ...freshFriends.map(c => ({ type: 'dm', id: getFriendId(c), name: c.display_name })),
  ].filter(f => !composeSearch || f.name.toLowerCase().includes(composeSearch.toLowerCase()));

  const groupsForCompose = groups.filter(g =>
    !composeSearch || g.name.toLowerCase().includes(composeSearch.toLowerCase())
  );

  const filteredConvs = conversations.filter(c =>
    !sidebarSearch || (c.display_name || '').toLowerCase().includes(sidebarSearch.toLowerCase())
  );
  const filteredGroups = groups.filter(g =>
    !sidebarSearch || (g.name || '').toLowerCase().includes(sidebarSearch.toLowerCase())
  );

  const hasContent = conversations.length > 0 || groups.length > 0;

  return (
    <Layout title="Messages">
      <div className="msg-page">
        {/* Sidebar */}
        <div className="msg-sidebar">
          <div className="msg-sidebar-header">
            <h2 className="msg-sidebar-title">Messages</h2>
            <button
              className="msg-compose-btn"
              onClick={() => { setComposing(true); setComposeSearch(''); setSidebarSearch(''); }}
              title="New message"
            >
              <PenSquare size={17} />
            </button>
          </div>

          {composing ? (
            <div className="msg-compose-panel">
              <div className="msg-compose-search-row">
                <Search size={14} color="#aaa" />
                <input
                  className="msg-compose-input"
                  placeholder="Search friends or groups…"
                  value={composeSearch}
                  onChange={e => setComposeSearch(e.target.value)}
                  autoFocus
                />
                <button className="msg-compose-cancel" onClick={() => setComposing(false)}>
                  <X size={15} />
                </button>
              </div>
              <div className="msg-conv-list">
                {allFriendsForCompose.length === 0 && groupsForCompose.length === 0 ? (
                  <div className="msg-empty-state">
                    {connections.length === 0 && groups.length === 0
                      ? 'Add friends or join a group to start messaging.'
                      : 'No results.'}
                  </div>
                ) : (
                  <>
                    {allFriendsForCompose.length > 0 && (
                      <>
                        <div className="msg-section-label">Friends</div>
                        {allFriendsForCompose.map(f => (
                          <button
                            key={f.id}
                            className={`msg-conv-row${active?.id === f.id && active?.type === 'dm' ? ' active' : ''}`}
                            onClick={() => openDM(f.id, f.name)}
                          >
                            <div className="msg-conv-avatar">{getInitials(f.name)}</div>
                            <div className="msg-conv-info">
                              <span className="msg-conv-name">{f.name}</span>
                              <span className="msg-conv-preview msg-conv-new">Direct message</span>
                            </div>
                          </button>
                        ))}
                      </>
                    )}
                    {groupsForCompose.length > 0 && (
                      <>
                        <div className="msg-section-label">Groups</div>
                        {groupsForCompose.map(g => (
                          <button
                            key={g.id}
                            className={`msg-conv-row${active?.id === g.id && active?.type === 'group' ? ' active' : ''}`}
                            onClick={() => openGroup(g.id, g.name)}
                          >
                            <div className="msg-conv-avatar msg-conv-avatar-group">
                              <Users size={16} />
                            </div>
                            <div className="msg-conv-info">
                              <span className="msg-conv-name">{g.name}</span>
                              <span className="msg-conv-preview">
                                {g.member_count} {g.member_count === 1 ? 'member' : 'members'}
                              </span>
                            </div>
                          </button>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
              {hasContent && (
                <div className="msg-search-wrap">
                  <Search size={14} color="#aaa" className="msg-search-icon" />
                  <input
                    className="msg-search-input"
                    placeholder="Search…"
                    value={sidebarSearch}
                    onChange={e => setSidebarSearch(e.target.value)}
                  />
                </div>
              )}

              {loadingConvs ? (
                <div className="msg-empty-state">Loading…</div>
              ) : !hasContent ? (
                <div className="msg-empty-state">
                  <MessageSquare size={32} color="#ccc" />
                  <p>No messages yet.</p>
                  <button className="msg-start-btn" onClick={() => setComposing(true)}>
                    Start a conversation
                  </button>
                </div>
              ) : (
                <div className="msg-conv-list">
                  {/* DM conversations */}
                  {filteredConvs.length > 0 && (
                    <>
                      <div className="msg-section-label">Direct</div>
                      {filteredConvs.map(conv => (
                        <button
                          key={conv.user_id}
                          className={`msg-conv-row${active?.id === conv.user_id && active?.type === 'dm' ? ' active' : ''}`}
                          onClick={() => openDM(conv.user_id, conv.display_name)}
                          onMouseEnter={() => setHoveredConv(conv.user_id)}
                          onMouseLeave={() => setHoveredConv(null)}
                        >
                          <div className="msg-conv-avatar">{getInitials(conv.display_name)}</div>
                          <div className="msg-conv-info">
                            <div className="msg-conv-name-row">
                              <span className={`msg-conv-name${conv.unread_count > 0 ? ' unread' : ''}`}>
                                {conv.display_name}
                              </span>
                              {hoveredConv === conv.user_id ? (
                                <button
                                  className="msg-delete-btn"
                                  onClick={e => handleDeleteDM(e, conv.user_id)}
                                  title="Delete conversation"
                                >
                                  <Trash2 size={13} />
                                </button>
                              ) : conv.last_at ? (
                                <span className="msg-conv-time">{timeAgo(conv.last_at)}</span>
                              ) : null}
                            </div>
                            <span className={`msg-conv-preview${conv.unread_count > 0 ? ' msg-conv-unread-preview' : ''}`}>
                              {conv.last_sender_id === currentUserId ? 'You: ' : ''}{conv.last_message || 'No messages yet'}
                            </span>
                          </div>
                          {conv.unread_count > 0 && (
                            <span className="msg-unread-badge">{conv.unread_count}</span>
                          )}
                        </button>
                      ))}
                    </>
                  )}

                  {/* Group conversations */}
                  {filteredGroups.length > 0 && (
                    <>
                      <div className="msg-section-label">Groups</div>
                      {filteredGroups.map(group => (
                        <button
                          key={group.id}
                          className={`msg-conv-row${active?.id === group.id && active?.type === 'group' ? ' active' : ''}`}
                          onClick={() => openGroup(group.id, group.name)}
                        >
                          <div className="msg-conv-avatar msg-conv-avatar-group">
                            <Users size={16} />
                          </div>
                          <div className="msg-conv-info">
                            <span className="msg-conv-name">{group.name}</span>
                            <span className="msg-conv-preview">
                              {group.member_count} {group.member_count === 1 ? 'member' : 'members'}
                            </span>
                          </div>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Chat panel */}
        <div className="msg-chat">
          {!active ? (
            <div className="msg-chat-empty">
              <MessageSquare size={40} color="#ddd" />
              <p>Select a conversation or start a new one</p>
              <button className="msg-start-btn" onClick={() => setComposing(true)}>
                <PenSquare size={15} /> New message
              </button>
            </div>
          ) : (
            <>
              <div className="msg-chat-header">
                <div className={`msg-chat-avatar${active.type === 'group' ? ' group' : ''}`}>
                  {active.type === 'group' ? <Users size={16} /> : getInitials(active.name)}
                </div>
                <div>
                  <div className="msg-chat-name">{active.name}</div>
                  {active.type === 'group' && (
                    <div className="msg-chat-sub">Group chat</div>
                  )}
                </div>
              </div>

              <div className="msg-chat-messages">
                {loadingMsgs ? (
                  <div className="msg-chat-loading">Loading…</div>
                ) : messages.length === 0 ? (
                  <div className="msg-chat-start">
                    <div className={`msg-chat-start-avatar${active.type === 'group' ? ' group' : ''}`}>
                      {active.type === 'group' ? <Users size={22} /> : getInitials(active.name)}
                    </div>
                    <p>
                      {active.type === 'group'
                        ? `Start the ${active.name} group chat!`
                        : `Say hi to ${active.name}!`}
                    </p>
                  </div>
                ) : (
                  messages.map((msg, i) => {
                    const isMe = msg.sender_id === currentUserId;
                    const prevMsg = messages[i - 1];
                    const showTime = !prevMsg || (new Date(msg.created_at) - new Date(prevMsg.created_at)) > 5 * 60000;
                    const showName = active.type === 'group' && !isMe &&
                      (!prevMsg || prevMsg.sender_id !== msg.sender_id);
                    return (
                      <React.Fragment key={msg.id}>
                        {showTime && (
                          <div className="msg-time-divider">{formatTime(msg.created_at)}</div>
                        )}
                        <div className={`msg-bubble-wrap${isMe ? ' me' : ''}`}>
                          {showName && (
                            <span className="msg-sender-name">{msg.sender_name}</span>
                          )}
                          <div className="msg-bubble-row">
                            {isMe && (
                              <button className="msg-delete-icon" onClick={() => handleDeleteMessage(msg.id)} title="Delete">
                                <Trash2 size={12} />
                              </button>
                            )}
                            <div className={`msg-bubble${isMe ? ' me' : ''}`}>
                              {msg.content}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              <form className="msg-input-row" onSubmit={handleSend}>
                <input
                  ref={inputRef}
                  className="msg-input"
                  placeholder={active.type === 'group' ? `Message ${active.name}…` : `Message ${active.name}…`}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  disabled={sending}
                />
                <button type="submit" className="msg-send-btn" disabled={!input.trim() || sending}>
                  <Send size={16} />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
